"use client";

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import Button, { geistSans } from "@/components/atoms/buttons/Button";
import { cn, getCheckoutBackRoute } from "@/lib/utils";
import { resolveCheckoutTitle } from "@/lib/checkout-personal-info";
import { createPaymentLocalisedSchema, PersonalInfoSchema } from "@/lib/validation/payment";
import Link from "next/link";
import { ChevronLeft } from "lucide-react";
import { useCheckoutStore } from "@/stores/checkout-store";
import { Locales } from "@/lib/i18n";
import { useRouter } from "next/navigation";
import { __, getPermalink } from "@/lib/i18n/functions";
import RadioButton from "@/components/atoms/radio-buttons";
import RadioShipping from "@/components/atoms/radio-shipping";
import { useEffect } from "react";
import { useCustomer } from "@/hooks/useCustomer";

type PersonalInfoFormProps = {
  locale: Locales;
  route: string;
};

const PersonalInfoForm = ({ locale, route }: PersonalInfoFormProps) => {
  const router = useRouter();
  const { personalInfoSchema } = createPaymentLocalisedSchema(locale);

  const { customer } = useCustomer();

  const setData = useCheckoutStore((state) => state.setData);
  const title = useCheckoutStore((state) => state.title);
  const firstName = useCheckoutStore((state) => state.firstName);
  const lastName = useCheckoutStore((state) => state.lastName);
  const email = useCheckoutStore((state) => state.email);
  const phone = useCheckoutStore((state) => state.phone);

  const form = useForm<PersonalInfoSchema>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      title: resolveCheckoutTitle(title, customer?.metadata?.title),
      firstName: firstName || "",
      lastName: lastName || "",
      email: email || "",
      phone: phone || "",
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    reset({
      title: resolveCheckoutTitle(title, customer?.metadata?.title),
      firstName: firstName || customer?.first_name || "",
      lastName: lastName || customer?.last_name || "",
      email: email || customer?.email || "",
      phone: phone || customer?.phone || "",
    });
  }, [title, firstName, lastName, email, phone, reset, customer]);

  const onSubmit = async (data: PersonalInfoSchema) => {
    setData(data);
    router.push(getPermalink(locale, "checkout", "delivery"));
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn("flex flex-col gap-5", geistSans.className)}>
        <FormField
          control={control}
          name="title"
          render={({ field }) => (
            <FormItem className="w-full gap-1">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">
                  {__("Civility", locale)} <span className="text-pink-primary">*</span>
                </FormLabel>
                <RadioShipping
                  locale={locale}
                  name="title"
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="max-w-none flex-row gap-4 max-sm:hidden"
                  items={[
                    { value: "mademoiselle", content: { title: __("Mrs.", locale) } },
                    { value: "madame", content: { title: __("Miss", locale) } },
                    { value: "monsieur", content: { title: __("Mr", locale) } },
                  ]}
                />
                <RadioButton
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  name="radio-title"
                  className="w-full justify-evenly min-sm:hidden"
                  options={[
                    { value: "mademoiselle", label: __("Mrs.", locale) },
                    { value: "madame", label: __("Miss", locale) },
                    { value: "monsieur", label: "Mr" },
                  ]}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex items-center gap-2.5 sm:gap-4">
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.lastName && !errors.firstName && "mb-5")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">
                    {__("First Name", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>

                  <FormControl>
                    <Input
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                      placeholder="John"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.firstName && !errors.lastName && "mb-5")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">
                    {__("Last Name", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                      placeholder="Doe"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-4">
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.phone && !errors.email && "mb-5")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">
                    {__("Email Address", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>

                  <FormControl>
                    <Input
                      type="email"
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                      placeholder={__("Email Address", locale)}
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="phone"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.email && !errors.phone && "mb-5")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">
                    {__("Phone Number", locale)} <span className="text-pink-primary">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                      placeholder="+32"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Link
          href={getCheckoutBackRoute(locale, route)}
          className="text-blue-brand flex w-full items-center justify-center gap-2.5 pr-1 text-start font-medium min-md:hidden"
        >
          <ChevronLeft size={24} aria-hidden="true" />
          {__("Return to the previous step", locale)}
        </Link>
        <Button
          type="submit"
          className="h-12 transition-transform duration-150 ease-in-out active:scale-95 min-sm:ml-auto min-sm:h-[60px]"
        >
          {__("Next step", locale)}
        </Button>
      </form>
    </Form>
  );
};

export default PersonalInfoForm;
